import { PropsWithChildren, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { Box, CircularProgress } from "@mui/material";
import { gql, useQuery } from "@apollo/client";
import { useUserContext } from "../../hooks/userContext";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { DesignerApplicationSteps } from "../../__generated__/types";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useHomeownerOnboardingAccess } from "../../hooks/useHomeownerOnboardingAccess";
import { getSidebarConfigWithFlags } from "../static-page-components/dashboard-layout/sidebar/SidebarConfigWithFlags";
import { PlanKey } from "../../pages/registration/homeowner-plan-selection-fieldset";
import { Pages } from "../../utils";

interface MembershipStatusQueryResponse {
  getAccountMembershipStatus?: {
    subscription?: string | null;
  } | null;
}

interface MembershipStatusQueryVariables {
  userId: string;
}

const GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY = gql`
  query GetAccountMembershipStatusForGuard($userId: ID!) {
    getAccountMembershipStatus(userId: $userId) {
      subscription
    }
  }
`;

const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
  const normalized = String(sub ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  if (!normalized || normalized === "free" || normalized === "basic") return "basic";
  if (
    normalized === "guildannually" ||
    normalized === "guildplus" ||
    normalized === "guildplusbeta" ||
    normalized === "guild+"
  ) {
    return "GuildPlus";
  }
  if (
    normalized === "guildmonthly" ||
    normalized === "guild" ||
    normalized === "stylefilemonthly" ||
    normalized === "stylefileannually"
  ) {
    return "Guild";
  }
  return "basic";
};

const resolveGuardPath = (pathname: string): string | null => {
  if (pathname === Pages.HOMEOWNER_PROFILE_SELF) return Pages.HOMEOWNER_PROFILE_SELF;
  if (pathname === Pages.DESIGNER_CONFESSIONS) return Pages.DESIGNER_CONFESSIONS;
  if (pathname === Pages.CLIENT_FINANCING) return Pages.CLIENT_FINANCING;
  if (pathname === Pages.PROJECT_REPORTING) return Pages.PROJECT_REPORTING;
  if (pathname === Pages.QUICK_LINKS) return Pages.QUICK_LINKS;
  if (pathname === Pages.PARTNERS) return Pages.PARTNERS;
  if (pathname === Pages.REFER) return Pages.REFER;
  if (pathname === Pages.CAMPAIGNS) return Pages.CAMPAIGNS;
  if (pathname === Pages.ASSETS) return Pages.ASSETS;
  if (pathname === Pages.GUILD_GUIDES || pathname.startsWith(`${Pages.GUILD_GUIDES}/`)) {
    return Pages.GUILD_GUIDES;
  }
  if (pathname === Pages.DESIGNER_FILES_HOMEOWNER) return Pages.DESIGNER_FILES_HOMEOWNER;
  if (pathname === Pages.MY_DESIGN_FILES) return Pages.MY_DESIGN_FILES;

  if (pathname === Pages.MESSAGES || pathname.startsWith(`${Pages.MESSAGES}/`)) {
    return Pages.MESSAGES;
  }
  if (pathname === Pages.MATCHES || pathname.startsWith(`${Pages.MATCHES}/`)) {
    return Pages.MATCHES;
  }
  if (pathname === Pages.MY_MATCHING_PROFILE || pathname.startsWith(`${Pages.MY_MATCHING_PROFILE}/`)) {
    return Pages.MY_MATCHING_PROFILE;
  }
  if (pathname === Pages.MY_PROJECTS || pathname.startsWith(`${Pages.MY_PROJECTS}/`)) {
    return Pages.MY_PROJECTS;
  }
  if (pathname === Pages.MY_IDEA_BOOKS || pathname.startsWith(`${Pages.MY_IDEA_BOOKS}/`)) {
    return Pages.MY_IDEA_BOOKS;
  }
  if (pathname === Pages.DESIGNER_FILES_PROJECTS || pathname.startsWith(`${Pages.DESIGNER_FILES_PROJECTS}/`)) {
    return Pages.MY_DESIGN_FILES;
  }
  if (pathname === Pages.ADD_DESIGNER_FILE || pathname.startsWith(`${Pages.ADD_DESIGNER_FILE}/`)) {
    return Pages.MY_DESIGN_FILES;
  }

  return null;
};

export const DashboardAccessGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { user, loading } = useUserContext();
  const {
    effectiveRole,
    effectiveTier,
    isDesigner,
    isHomeowner,
    isAdmin,
    canAccessAdminFeatures,
    canAccessFeature,
  } = useEffectiveRole();
  const { application } = useDesignerApplicationContext();
  const featureFlags = useFeatureFlags();
  const onboardingAccess = useDesignerOnboardingAccess();
  const homeownerOnboardingAccess = useHomeownerOnboardingAccess();

  const { data: membershipStatusData, loading: membershipStatusLoading } = useQuery<
    MembershipStatusQueryResponse,
    MembershipStatusQueryVariables
  >(GET_ACCOUNT_MEMBERSHIP_STATUS_QUERY, {
    variables: { userId: user?.id || "" },
    skip: !user?.id,
    fetchPolicy: "cache-and-network",
  });

  const membershipSubscription = membershipStatusData?.getAccountMembershipStatus?.subscription;
  const rawSubscription = typeof membershipSubscription === "string" && membershipSubscription.trim()
    ? membershipSubscription
    : typeof user?.subscription === "string"
      ? user.subscription
      : (user?.subscription as any)?.homeownerValue ||
        (user?.subscription as any)?.designerValue ||
        (user?.subscription as any)?.value ||
        (user?.subscription as any)?.tier ||
        "basic";

  const actualPlan = mapSubscriptionToPlanKey(rawSubscription);
  const effectivePlan: PlanKey = !isDesigner && effectiveTier
    ? (effectiveTier === "Guild+" ? "GuildPlus" : effectiveTier === "Basic" ? "basic" : effectiveTier) as PlanKey
    : actualPlan;
  const isAdminHomeownerPreview =
    canAccessAdminFeatures &&
    effectiveRole === "Homeowner" &&
    (user?.accountType ?? "").toLowerCase() !== "homeowner";

  const isDesignerMembershipActive = isDesigner && application?.status === DesignerApplicationSteps.MembershipActive;

  const { guardPath, accessMap } = useMemo(() => {
    const path = resolveGuardPath(location.pathname);
    if (!path) {
      return { guardPath: null as string | null, accessMap: new Map<string, boolean>() };
    }

    const effectiveHomeownerOnboardingAccess = !isDesigner
      ? isAdminHomeownerPreview
        ? {
            matchingProfileUnlocked: true,
            bioUnlocked: true,
          }
        : {
            matchingProfileUnlocked: homeownerOnboardingAccess.matchingProfileUnlocked,
            bioUnlocked: homeownerOnboardingAccess.bioUnlocked,
          }
      : undefined;

    const config = getSidebarConfigWithFlags({
      isDesigner,
      userPlan: effectivePlan,
      isDesignerApproved: isDesignerMembershipActive,
      featureFlags,
      onboardingAccess: isDesigner ? onboardingAccess : undefined,
      homeownerOnboardingAccess: effectiveHomeownerOnboardingAccess,
      canAccessFeature,
    });

    const map = new Map<string, boolean>();
    const visit = (items: any[]) => {
      for (const item of items) {
        if (item?.path) {
          map.set(item.path, Boolean(item.locked));
        }
        if (Array.isArray(item?.subItems)) {
          visit(item.subItems);
        }
      }
    };
    visit(config as any[]);
    return { guardPath: path, accessMap: map };
  }, [
    location.pathname,
    isAdminHomeownerPreview,
    isDesigner,
    effectivePlan,
    isDesignerMembershipActive,
    featureFlags,
    onboardingAccess,
    homeownerOnboardingAccess.matchingProfileUnlocked,
    homeownerOnboardingAccess.bioUnlocked,
    canAccessFeature,
  ]);

  const isLoading = loading || membershipStatusLoading || (isDesigner && onboardingAccess.loading);

  if (!guardPath) {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: "#00A06D" }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to={Pages.LOGIN} replace />;
  }

  if (
    canAccessAdminFeatures &&
    guardPath === Pages.DESIGNER_FILES_HOMEOWNER
  ) {
    return <>{children}</>;
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  const isLocked = accessMap.get(guardPath);
  const hasAccess = isLocked === false;

  if (!hasAccess) {
    const needsUpgrade = isHomeowner && effectivePlan === "basic";
    const redirectTo =
      guardPath === Pages.HOMEOWNER_PROFILE_SELF && needsUpgrade
        ? Pages.HOMEOWNER_MEMBERSHIP_PRICING
        : Pages.DASHBOARD;
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};
